import axios from 'axios';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { API_REQUEST_TIMEOUT_MS, API_URLs, BASE_URL } from './http.service';
import { clearAccessToken, emitAuthFailure, getAccessToken, setAccessToken } from './token.store';

const getApiErrorMessage = (error) => {
  const payload = error?.response?.data;
  if (typeof payload?.errors === 'string') return payload.errors;
  if (Array.isArray(payload?.errors)) {
    return payload.errors
      .map((item) => item?.message || item?.msg || item)
      .filter(Boolean)
      .join(', ');
  }
  if (typeof payload?.message === 'string') return payload.message;
  if (typeof payload?.error === 'string') return payload.error;
  return error?.message || 'Request failed. Please try again.';
};

const showApiErrorToast = (error, fallbackMessage) => {
  toast.error(fallbackMessage || getApiErrorMessage(error), {
    id: `api-error-${error?.config?.url || 'request'}`,
  });
};

const useAxiosInstance = () => {
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
      timeout: API_REQUEST_TIMEOUT_MS,
    });
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
      });
      failedQueue = [];
    };

    instance.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            try {
              const token = await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              });
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            } catch (err) {
              return Promise.reject(err);
            }
          }

          originalRequest._retry = true;
          isRefreshing = true;

          return new Promise((resolve, reject) => {
            axios
              .post(
                API_URLs.refreshToken,
                {},
                {
                  withCredentials: true,
                  timeout: API_REQUEST_TIMEOUT_MS,
                }
              )
              .then(({ data }) => {
                const newAccess = data?.data?.accessToken;
                if (newAccess) setAccessToken(newAccess);
                processQueue(null, newAccess);
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                resolve(instance(originalRequest));
              })
              .catch((err) => {
                processQueue(err, null);
                // The refresh cookie is gone, expired or rejected — this session
                // is over. Drop the stale access token and tell AuthProvider, or
                // context keeps reporting isLoggedIn while every call 401s.
                clearAccessToken();
                emitAuthFailure();
                // Silent on the boot probe: an anonymous first visit takes this
                // exact path, and "your session could not be refreshed" is a
                // confusing thing to show someone who never had one.
                if (!originalRequest._silent) {
                  showApiErrorToast(
                    err,
                    'Your session could not be refreshed. Please sign in again if this continues.'
                  );
                }
                reject(err);
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }
        if (!originalRequest?._silent) showApiErrorToast(error);
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  return axiosInstance;
};

// Default export — used as: const api = useAPI()
const useAPI = () => {
  const axiosInstance = useAxiosInstance();

  return useMemo(
    () => ({
      login: async (body) => {
        const { data } = await axios.post(API_URLs.login, body, {
          withCredentials: true,
          timeout: API_REQUEST_TIMEOUT_MS,
        });
        return data;
      },
      // Whether "Sign in with Microsoft" is offered (TASK-2026-10-13-001).
      // Public, so plain axios like login — no refresh interceptor, no toast.
      // Any failure means "not offered": the password form still works.
      getSsoConfig: async () => {
        try {
          const { data } = await axios.get(API_URLs.ssoConfig, {
            withCredentials: true,
            timeout: API_REQUEST_TIMEOUT_MS,
          });
          return { enabled: data?.data?.enabled === true };
        } catch {
          return { enabled: false };
        }
      },
      register: async (body) => {
        const { data } = await axios.post(API_URLs.register, body, {
          withCredentials: true,
          timeout: API_REQUEST_TIMEOUT_MS,
        });
        return data;
      },
      // `_silent` on both: signing out and probing for an existing session are
      // the two calls whose failure the user should never see as a red toast.
      // Logout clears client state either way, and a failed probe just means
      // "not signed in".
      logout: async () => {
        const { data } = await axiosInstance.post(API_URLs.logout, {}, { _silent: true });
        return data;
      },
      getCurrentUser: async ({ silent = false } = {}) => {
        const { data } = await axiosInstance.get(API_URLs.currentUser, { _silent: silent });
        return data;
      },
      // Change Password (TASK-2026-10-12-002). On the authenticated instance,
      // so an expired access token is refreshed and retried as for any call.
      // `_silent` because the modal shows its own errors — a wrong old password
      // inline on its field — and the interceptor's toast would duplicate them.
      changePassword: async (body) => {
        const { data } = await axiosInstance.post(API_URLs.changePassword, body, {
          _silent: true,
        });
        return data;
      },
      // Forgot Password — DISABLED by TASK-2026-10-12-002 (the backend routes
      // answer 404, and nothing renders ForgotPassword.jsx). Uncomment these and
      // their URLs in http.service.jsx to restore.
      //
      // The three Forgot Password calls go out on plain axios, like login: the
      // visitor is signed out, so the interceptor's refresh-on-401 has nothing
      // to refresh, and the form shows its own errors rather than the
      // interceptor's generic toast.
      // forgotPassword: async (body) => {
      //   const { data } = await axios.post(API_URLs.forgotPassword, body, {
      //     timeout: API_REQUEST_TIMEOUT_MS,
      //   });
      //   return data;
      // },
      // verifyOtp: async (body) => {
      //   const { data } = await axios.post(API_URLs.verifyOtp, body, {
      //     timeout: API_REQUEST_TIMEOUT_MS,
      //   });
      //   return data;
      // },
      // resetPassword: async (body) => {
      //   const { data } = await axios.post(API_URLs.resetPassword, body, {
      //     timeout: API_REQUEST_TIMEOUT_MS,
      //   });
      //   return data;
      // },
      getAllUsers: async (params = {}) => {
        const { data } = await axiosInstance.get(API_URLs.users, { params });
        return data;
      },
      getCronJobHistory: async (params = {}) => {
        const { data } = await axiosInstance.get(API_URLs.cronJobHistory, { params });
        return data;
      },
      getFabricDataUpdateTimes: async () => {
        const { data } = await axiosInstance.get(API_URLs.fabricDataUpdateTimes);
        return data;
      },
      addNewUser: async (body) => {
        const payload = { ...body, roleId: body.roleId || body.role };
        const { data } = await axiosInstance.post(API_URLs.users, payload);
        return data;
      },
      editUser: async (userId, body) => {
        const payload = { ...body, roleId: body.roleId || body.role };
        const { data } = await axiosInstance.patch(`${API_URLs.users}/${userId}`, payload);
        return data;
      },
      deleteUser: async (userId) => {
        const { data } = await axiosInstance.delete(`${API_URLs.users}/${userId}`);
        return data;
      },
    }),
    [axiosInstance]
  );
};

export default useAPI;
